// filepath: src/pages/matrimony/components/sectionForms/ProfessionalEditForm.tsx
import React from "react";
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  VStack,
} from "@chakra-ui/react";
import { commonInputProps, commonSelectProps } from "src/pages/matrimony/pages/matrimonyForm.ui";
import { FIELD_LABELS } from "src/utils/matrimonyFieldLabels";
import {
  EMPLOYMENT_SECTOR_OPTIONS,
  HIGHEST_EDUCATION_OPTIONS,
  SOCIAL_PLATFORM_OPTIONS,
} from "src/utils/matrimonyOptions";
import type { ProfessionalState } from "src/types/marriageProfileSectionEdit.types";
import type { BaseSectionFormProps, SocialLinkProps } from "./sectionFormProps";

type Props = BaseSectionFormProps & SocialLinkProps & { values: ProfessionalState };

const ProfessionalEditForm: React.FC<Props> = ({
  values,
  errors: _errors,
  setField,
  updateSocialLink,
  addSocialLink,
  removeSocialLink,
}) => (
  <>
    <FormControl>
      <FormLabel>{FIELD_LABELS.HIGHEST_EDUCATION}</FormLabel>
      <Select
        {...commonSelectProps}
        value={values.highestEducation || ""}
        onChange={(e) => setField("highestEducation", e.target.value)}
        placeholder="Select highest education"
      >
        {HIGHEST_EDUCATION_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </Select>
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.COLLEGE_UNIVERSITY}</FormLabel>
      <Input
        {...commonInputProps}
        value={values.collegeOrUniversity || ""}
        onChange={(e) => setField("collegeOrUniversity", e.target.value)}
      />
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.EMPLOYMENT_SECTOR}</FormLabel>
      <Select
        {...commonSelectProps}
        value={values.employmentSector || ""}
        onChange={(e) => setField("employmentSector", e.target.value)}
        placeholder="Select your employment sector"
      >
        {EMPLOYMENT_SECTOR_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </Select>
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.DESIGNATION}</FormLabel>
      <Input
        {...commonInputProps}
        value={values.designation || ""}
        onChange={(e) => setField("designation", e.target.value)}
        placeholder="e.g. Senior Engineer"
      />
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.COMPANY}</FormLabel>
      <Input
        {...commonInputProps}
        value={values.employerName || ""}
        onChange={(e) => setField("employerName", e.target.value)}
      />
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.ANNUAL_INCOME}</FormLabel>
      <Input
        {...commonInputProps}
        type="number"
        value={values.annualIncome || ""}
        onChange={(e) => setField("annualIncome", e.target.value)}
      />
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.SOCIAL_MEDIA_LINKS}</FormLabel>
      <VStack align="stretch" spacing={3}>
        {(Array.isArray(values.socialMediaLinksList)
          ? (values.socialMediaLinksList as any)
          : []
        ).map((row: any, idx: number) => (
          <HStack key={row?.id != null ? String(row.id) : idx} spacing={2} align="start">
            <Select
              {...commonSelectProps}
              value={String(row?.socialMediaPlatforms ?? row?.platform ?? "")}
              onChange={(e) => updateSocialLink(idx, { socialMediaPlatforms: e.target.value })}
              placeholder="Platform"
            >
              {SOCIAL_PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
            <Input
              {...commonInputProps}
              value={String(row?.link ?? row?.url ?? "")}
              onChange={(e) => updateSocialLink(idx, { link: e.target.value })}
              placeholder="https://..."
            />
            <Button variant="outline" onClick={() => removeSocialLink(idx)}>
              Remove
            </Button>
          </HStack>
        ))}
        <Button variant="outline" onClick={addSocialLink}>
          Add Link
        </Button>
      </VStack>
    </FormControl>
  </>
);

export default ProfessionalEditForm;
